import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';

import {
  trackScsScopeDiffCheckBulkUpdateSuccess,
  trackScsScopeDiffCheckBulkUpdateError,
  trackScsScopeDiffCheckDownloaded,
  trackScsScopeDiffCheckCancelled,
} from '../../../utils/analytics';
import { scsBulkedit } from '../../../utils/service-calls/scs';
import { toGeo } from '../../../utils/static/scs-geo-iso-map';
import { ButtonRed, TableRowClick, ButtonBlack } from '../../reusable';
import Modal from '../../reusable/CommonModal/Modal';

const getHigherScopeTypes = (scopeType) => {
  const scopeHierarchyArr = ['STORE', 'COUNTRY', 'GEO', 'GLOBAL'];
  const index = scopeHierarchyArr.indexOf(scopeType);
  const preceding = scopeHierarchyArr.slice(0, index);
  return preceding;
};

export const filterStoresWithDiffVal = (
  value,
  scopeType,
  countries,
  report,
  storeList
) => {
  const isStoreInCountry = (store) => {
    if (countries.length === 0) {
      return true; // If no countries are specified, include all stores
    }
    return countries.filter((country) => store.startsWith(country)).length > 0;
  };
  const storeNameMap = new Map(
    storeList.map((store) => [
      `${store.address.country}-${store.storeNumber}`,
      store.name,
    ])
  );
  const storeGeoMap = new Map(
    storeList.map((store) => [
      `${store.address.country}-${store.storeNumber}`,
      toGeo(store),
    ])
  );
  const higherScopes = getHigherScopeTypes(scopeType);
  const result = report
    .filter((item) => String(item.value) !== String(value))
    .filter((item) => higherScopes.includes(item.scopeType))
    .flatMap((item) => item.stores
        .filter((store) => isStoreInCountry(store))
        .map((store) => {
          // Extract country and storeNumber from store identifier as store is in the format 'country-storeNumber'
          const country = store.split('-')[0];
          const storeNumber = store.split('-')[1];
          const storeName = storeNameMap.get(store) || '';
          const geo = storeGeoMap.get(store) || '';
          return {
            country,
            geo,
            scopeType: item.scopeType,
            storeConfig: item.value,
            storeName,
            storeNumber,
          };
        }));
  return result;
};

const ScopeDiffCheck = ({
  isOpen,
  setIsOpen,
  diffDataMap,
  keyName,
  isLoading,
  scsRegion,
  accessToken,
  userInfo,
  targetValue,
  justification,
  originalSaveData,
}) => {
  // State for managing selected rows
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [isBulkUpdateLoading, setIsBulkUpdateLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Calculate if all rows are selected
  const totalRows = diffDataMap[keyName] ? diffDataMap[keyName].length : 0;
  const isAllSelected = totalRows > 0 && selectedRows.size === totalRows;

  // Handle individual row selection
  const handleRowSelect = useCallback((rowIndex, isChecked) => {
    setSelectedRows((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (isChecked) {
        newSelected.add(rowIndex);
      } else {
        newSelected.delete(rowIndex);
      }
      return newSelected;
    });
  }, []);

  // Handle select all/deselect all
  const handleSelectAll = useCallback(
    (isChecked) => {
      if (isChecked) {
        const allIndices = Array.from({ length: totalRows }, (_, i) => i);
        setSelectedRows(new Set(allIndices));
      } else {
        setSelectedRows(new Set());
      }
    },
    [totalRows]
  );

  // Transform selected data for bulk update
  const transformSelectedDataForBulkUpdate = () => {
    const selectedData
      = selectedRows.size > 0
        ? (diffDataMap[keyName] || []).filter((_, index) => selectedRows.has(index))
        : diffDataMap[keyName] || [];

    // Group data by scope type
    const groupedByScope = selectedData.reduce((acc, item) => {
      const { scopeType } = item;
      if (!acc[scopeType]) {
        acc[scopeType] = [];
      }
      acc[scopeType].push(item);
      return acc;
    }, {});

    // Create data array for each scope type
    const bulkData = [];

    // First, add the original save data if provided
    if (originalSaveData?.body) {
      const originalData = {
        key: originalSaveData.body.key,
        scopeInfo: [{
          hierarchy: originalSaveData.body.hierarchy,
          scope: originalSaveData.body.scope,
        }],
        scopeType: originalSaveData.body.scopeType,
        value: originalSaveData.body.value,
      };
      bulkData.push(originalData);
    }

    Object.entries(groupedByScope).forEach(([scopeType, items]) => {
      // Create a Map to deduplicate scope entries
      const scopeMap = new Map();
      items.forEach((item) => {
        const scope
          = scopeType === 'STORE'
            ? `${item.country}-${item.storeNumber}`
            : item.country;

        // Use scope as key to deduplicate
        if (!scopeMap.has(scope)) {
          scopeMap.set(scope, {
            hierarchy: {
              brand: 'NIKE', // Default brand, could be made configurable
              country: item.country,
              geo: item.geo,
            },
            scope,
          });
        }
      });

      // Convert Map values to array
      const scopeInfo = Array.from(scopeMap.values());

      bulkData.push({
        key: keyName,
        scopeInfo,
        scopeType,
        value: targetValue, // Use the target value for bulk update
      });
    });

    return bulkData;
  };

  // Handle bulk update
  const handleBulkUpdate = async () => {
    if (selectedRows.size === 0) {
      setErrorMessage('Please select at least one row to update.');
      return;
    }

    if (!justification.trim()) {
      setErrorMessage('Please provide a justification for this bulk update.');
      return;
    }

    setIsBulkUpdateLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const bulkData = transformSelectedDataForBulkUpdate();
      const body = {
        changeRequest: {
          justification,
          requestedBy: userInfo?.email || 'unknown@nike.com',
        },
        data: bulkData,
      };

      const response = await scsBulkedit(
        accessToken,
        body,
        scsRegion || 'use1'
      );
      if (response.body.success && response.body.success.length > 0) {
        // Track successful bulk update
        trackScsScopeDiffCheckBulkUpdateSuccess({
          keyName,
          selectedCount: selectedRows.size,
          successCount: response.body.success.length,
          userEmail: userInfo?.email,
        });

        setSuccessMessage(
          `Successfully updated ${response.body.success.length} configuration(s).`
        );
        setIsOpen(false); // Close the modal
        // No need to call save() since original data is included in bulk update
      } else if (response.body.failure && response.body.failure.length > 0) {
        // Track partial failure
        trackScsScopeDiffCheckBulkUpdateError({
          errorMessage: 'Partial failure - some updates failed',
          keyName,
          selectedCount: selectedRows.size,
          userEmail: userInfo?.email,
        });

        setErrorMessage(
          `Some updates failed. Please check the console for details.`
        );
      }
    } catch (error) {
      // Track bulk update error
      trackScsScopeDiffCheckBulkUpdateError({
        errorMessage: error.message || 'Unknown error',
        keyName,
        selectedCount: selectedRows.size,
        userEmail: userInfo?.email,
      });

      setErrorMessage('Bulk update failed. Please try again.');
    } finally {
      setIsBulkUpdateLoading(false);
    }
  };

  // Download the diff data as a CSV file
  const download = async (key, data) => {
    // Get selected data or all data if none selected
    const selectedData
      = selectedRows.size > 0
        ? data.filter((_, index) => selectedRows.has(index))
        : data;

    // Track download
    trackScsScopeDiffCheckDownloaded({
      downloadedCount: selectedData.length,
      keyName: key,
      totalConflicts: data.length,
    });

    const csvConfigFields = [
      'Key',
      'Store Number',
      'Store Name',
      'Country',
      'Value',
      'Scope',
    ];
    const missingValueCheck = (pathCheck, invField = ' ') => (pathCheck ? JSON.stringify(pathCheck.replace(/,/g, ' ')) : invField);
    const scsConfigSchema = (config) => [
      missingValueCheck(key),
      missingValueCheck(config.storeNumber),
      missingValueCheck(config.storeName),
      missingValueCheck(config.country),
      missingValueCheck(config.storeConfig),
      missingValueCheck(config.scopeType),
      '\r',
    ];
    const transformConfigData = (conf) => {
      let csvContent = '';
      conf.forEach((config) => {
        csvContent = csvContent.concat(scsConfigSchema(config));
      });
      return csvContent;
    };
    const csvContent = transformConfigData(selectedData);
    const csv = document.createElement('a');
    const encodedUri = encodeURI(csvContent);
    csv.setAttribute('href', encodedUri);
    csv.href = URL.createObjectURL(
      new Blob([`${csvConfigFields}\n${csvContent}\n`], {
        type: 'text/csv;charset=utf-8',
      })
    );
    csv.setAttribute('download', `ConfigList.csv`);
    document.body.appendChild(csv);
    csv.click();
    document.body.removeChild(csv);
  };

  const countStoresCountriesGeos = (data) => data.reduce((acc, item) => {
      const scope = item.scopeType;
      acc[scope] = (acc[scope] || 0) + 1;
      return acc;
    }, {});

  const columns = [
    {
      accessor: () => '',
      Cell: (rowInfo) => (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            padding: '8px',
            width: '100%',
          }}
        >
          <input
            checked={selectedRows.has(rowInfo.index)}
            id={`scope-diff-checkbox-${rowInfo.index}`}
            style={{
              cursor: 'pointer',
              height: '16px',
              width: '16px',
            }}
            type="checkbox"
            onChange={(e) => {
              e.stopPropagation();
              handleRowSelect(rowInfo.index, e.target.checked);
            }}
          />
        </div>
      ),
      Header: () => (
        <button
          style={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            fontSize: 'inherit',
            textDecoration: 'underline',
          }}
          type="button"
          onClick={() => handleSelectAll(!isAllSelected)}
        >
          {isAllSelected ? 'Deselect All' : 'Select All'}
        </button>
      ),
      id: 'select',
      sortable: false,
      width: 120,
    },
    {
      accessor: (data) => data.storeNumber,
      Header: 'Store #',
      id: 'Store #',
    },
    {
      accessor: (data) => data.storeName,
      Header: 'Store Name',
      id: 'Store Name',
    },
    {
      accessor: (data) => data.country,
      Header: 'Country',
      id: 'Country',
    },
    {
      accessor: (data) => data.storeConfig,
      Header: 'Value',
      id: 'Store Config Value',
    },
    {
      accessor: (data) => data.scopeType,
      Header: 'Scope',
      id: 'Store Scope',
    },
  ];

  const scopeCounts = countStoresCountriesGeos(diffDataMap[keyName] || []);
  const scopeLevels = [
    { display: 'STORE Level', label: 'STORE' },
    { display: 'COUNTRY Level', label: 'COUNTRY' },
    { display: 'GEO Level', label: 'GEO' },
  ];
  const scopeDiffString = scopeLevels
    .filter(({ label }) => scopeCounts[label] > 0)
    .map(
      ({ label, display }) => `${scopeCounts[label]} stores at the ${display}`
    )
    .join(', ');

  // Handle cancel/close
  const handleCancel = () => {
    trackScsScopeDiffCheckCancelled({
      conflictsCount: totalRows,
      keyName,
    });
    setIsOpen(false);
  };

  return (
    <article className="ncss-col-sm-12 ta-sm-l va-sm-t">
      <Modal
        isOpen={isOpen}
        loading={isLoading}
        title="Config Value Mismatch Detected"
        onClose={handleCancel}
      >
        <br />
        <p className="headline-5 ta-sm-c">
          {`Value differences detected for config ${keyName}${
            scopeDiffString ? ` for ${scopeDiffString}` : ''
          }`}
        </p>
        <br />
        <TableRowClick
          showPagination
          columns={columns}
          data={diffDataMap[keyName] || []}
          defaultPageSize={5}
          handleClick={() => {}}
        />
        <br />
        <br />
        {/* Error Message */}
        {errorMessage && (
          <article className="ncss-col-sm-12 va-sm-t">
            <p className="headline-5 ta-sm-c text-color-error">
              {errorMessage}
            </p>
          </article>
        )}
        {/* Success Message */}
        {successMessage && (
          <article className="ncss-col-sm-12 va-sm-t">
            <p className="headline-5 ta-sm-c text-color-success">
              {successMessage}
            </p>
          </article>
        )}
        <br />
        <p className="headline-5 ta-sm-c text-color-accent">
          Warning: All the Values Selected will be Updated.
        </p>
        <article className="ncss-col-sm-12">
          <article className="ncss-col-sm-2">
            <ButtonBlack
              confirm={false}
              label={
                selectedRows.size > 0
                  ? `Download Selected (${selectedRows.size})`
                  : 'Download All'
              }
              onClick={() => {
                download(keyName, diffDataMap[keyName]);
              }}
            />
          </article>
          <article className="ncss-col-sm-10 ta-sm-r">
            <article className="ncss-col-sm-10 ta-sm-r">
              <ButtonRed
                confirm={false}
                isDisabled={
                  isBulkUpdateLoading || !justification || !justification.trim()
                }
                label={
                  isBulkUpdateLoading ? 'Updating...' : 'Bulk Update Selected'
                }
                onClick={() => {
                  handleBulkUpdate();
                }}
              />
            </article>
            <article className="ncss-col-sm-2 ta-sm-r">
              <ButtonBlack label="Cancel" onClick={handleCancel} />
            </article>
          </article>
        </article>
      </Modal>
      <br />
    </article>
  );
};

ScopeDiffCheck.propTypes = {
  accessToken: PropTypes.string.isRequired,
  diffDataMap: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  justification: PropTypes.string.isRequired,
  keyName: PropTypes.string.isRequired,
  originalSaveData: PropTypes.shape({
    body: PropTypes.shape({
      hierarchy: PropTypes.shape({}),
      key: PropTypes.string,
      scope: PropTypes.string,
      scopeType: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    }),
  }),
  scsRegion: PropTypes.string.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  targetValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]).isRequired,
  userInfo: PropTypes.shape({
    email: PropTypes.string,
  }).isRequired,
};

ScopeDiffCheck.defaultProps = {
  isLoading: false,
  originalSaveData: null,
};

export default ScopeDiffCheck;
